// ErrorsPanelV3 — surfaces failed trigger runs above the data surface.
//
// Renders only when there's at least one error in the last 24h. Clicking
// a row reveals the error message + offers archive (acknowledge, hide
// from list) or replay (re-fire the trigger on the same input event).
//
// Mirrors V2 errors-tab structure but inline — operator doesn't navigate
// to a tab to see what's broken.

import React, { useState } from 'react';
import type { Trigger, TriggerRun } from '@datahub/shared';
import { archiveTriggerRun, reprocessEvent } from '../../api';
import { useToast } from '../Toast';

export function ErrorsPanelV3({ errors, triggers, sourceId, onChanged }: {
  errors: TriggerRun[];
  triggers: Trigger[];
  sourceId: string;
  onChanged: () => void;
}): React.ReactElement | null {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  if (errors.length === 0) return null;

  const triggersById = new Map(triggers.map(t => [t.trigger_id, t]));

  async function ack(run: TriggerRun): Promise<void> {
    setBusyId(run.run_id);
    try {
      await archiveTriggerRun(sourceId, run.run_id);
      toast.show('Error acknowledged.', { kind: 'success' });
      onChanged();
    } catch (e) {
      toast.show(`Acknowledge failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function retry(run: TriggerRun): Promise<void> {
    setBusyId(run.run_id);
    try {
      await reprocessEvent(sourceId, run.event_id);
      toast.show('Event reprocessed — refreshing.', { kind: 'success' });
      onChanged();
    } catch (e) {
      toast.show(`Retry failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{
      marginBottom: 18,
      border: '1px solid oklch(0.78 0.10 30)',
      background: 'oklch(0.97 0.04 30 / 0.5)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'oklch(0.36 0.16 30)', fontFamily: 'var(--ui)', fontSize: 13,
          fontWeight: 500,
        }}
      >
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'oklch(0.42 0.16 30)' }}>{expanded ? '▾' : '▸'}</span>
        <span style={{ fontSize: 14 }}>⚠</span>
        <strong>{errors.length} trigger error{errors.length === 1 ? '' : 's'}</strong>
        <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--ink-2)' }}>
          last 24h · click to {expanded ? 'collapse' : 'review'}
        </span>
        <span style={{ flex: 1 }} />
      </button>
      {expanded && (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {errors.map(run => {
            const t = triggersById.get(run.trigger_id);
            const busy = busyId === run.run_id;
            return (
              <div
                key={run.run_id}
                style={{
                  padding: '8px 10px',
                  background: 'var(--bg-elev)',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  display: 'flex', flexDirection: 'column', gap: 4,
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--ink)' }}>
                    {t?.name ?? '(deleted trigger)'}
                  </strong>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)' }}>
                    {t?.action.type ?? 'unknown'} · {new Date(run.ran_at).toLocaleString()}
                  </span>
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    onClick={() => { void retry(run); }}
                    disabled={busy}
                    className="btn sm ghost"
                    style={{ padding: '2px 10px', fontSize: 11 }}
                    title="Re-fire all triggers on this event (idempotent — same event_key)"
                  >retry</button>
                  <button
                    type="button"
                    onClick={() => { void ack(run); }}
                    disabled={busy}
                    className="btn sm ghost"
                    style={{ padding: '2px 10px', fontSize: 11 }}
                    title="Hide this error from the list. The run record stays in /api/audit for the trail."
                    aria-label="Dismiss this error"
                  >dismiss</button>
                </div>
                <code style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--danger)', wordBreak: 'break-word' }}>
                  {run.error ?? '(no error message)'}
                </code>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)' }}>
                  event_id: {run.event_id} · run: {run.run_id}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
